import { MoreHorizontal } from "lucide-react";
import type { AIProvider } from "@/services/admin-api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ProviderTableProps {
  providers: AIProvider[];
  isTogglePending: boolean;
  onToggle: (provider: AIProvider) => void;
  onEdit: (provider: AIProvider) => void;
  onUpdateKey: (provider: AIProvider) => void;
  onRevealKey: (provider: AIProvider) => void;
  onRateLimits: (provider: AIProvider) => void;
  onDelete: (provider: AIProvider) => void;
}

export function ProviderTable({
  providers,
  isTogglePending,
  onToggle,
  onEdit,
  onUpdateKey,
  onRevealKey,
  onRateLimits,
  onDelete,
}: ProviderTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Provider</TableHead>
          <TableHead>Adapter</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Rate Limits</TableHead>
          <TableHead>Base URL</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {providers.map((provider) => (
          <TableRow key={provider.id}>
            <TableCell className="whitespace-nowrap">
              <div className="font-medium">{provider.name}</div>
              <div className="text-sm text-muted-foreground">{provider.id}</div>
            </TableCell>
            <TableCell className="whitespace-nowrap text-sm">{provider.id}</TableCell>
            <TableCell className="whitespace-nowrap">
              <button onClick={() => onToggle(provider)} disabled={isTogglePending}>
                <Badge
                  variant={provider.isActive ? "success" : "outline"}
                  className="cursor-pointer"
                >
                  {provider.isActive ? "Active" : "Inactive"}
                </Badge>
              </button>
            </TableCell>
            <TableCell className="whitespace-nowrap text-sm">
              {provider.rpmLimit ? `${provider.rpmLimit} RPM` : "Unlimited"}
              {provider.rpsLimit && ` / ${provider.rpsLimit} RPS`}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
              {provider.baseUrl}
            </TableCell>
            <TableCell className="whitespace-nowrap">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(provider)}>Edit</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onUpdateKey(provider)}>
                    Update Key
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onRevealKey(provider)}>
                    Reveal Key
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onRateLimits(provider)}>
                    Rate Limits
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete(provider)}
                    className="text-red-600 focus:text-red-600"
                  >
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
